import type {
  PickupAttemptStatus,
  PickupOpenOpportunityBadge
} from '@/types/pickupOpportunity';
import type {
  PickupVerificationMismatch,
  PickupVerificationStatus
} from '@/types/pickupVerification';
import type { PickupCanonicalLoanInput } from './pickupReconciliationPolicy';

export type {
  PickupVerificationMismatch,
  PickupVerificationStatus
} from '@/types/pickupVerification';

// Pipeline Status Report Types

export interface MilestoneHistoryItem {
  name: string;
  done: boolean;
  doneDate: string | null;
  doneBy: string | null;
  // Stage-entry timestamp. Populated by the Encompass sync for completed items
  // AND the current in-progress milestone; ~100% present on done items. Drives
  // time-in-stage/velocity (per-item doneDate ~= startDate, so use consecutive
  // stages' startDates, not one item's own two dates).
  startDate?: string | null;
  // Synced Encompass duration (sparse ~60% — do not rely on it for velocity).
  duration?: string | number | null;
  id?: string | null;
}

export interface PipelineLoan {
  encompass_loan_id: string;
  trans_details_loan_number: string | null;
  borrower_first_name: string | null;
  borrower_last_name: string | null;
  loan_officer_first_name: string | null;
  loan_officer_last_name: string | null;
  trans_details_loan_amt: number | null;
  subject_property_state?: string | null;
  rate_lock_buy_side_lock_date: string | null;
  rate_lock_sell_side_lock_date?: string | null;
  rate_lock_request_lock_date?: string | null;
  rate_lock_buy_side_lock_expires: string | null;
  rate_lock_status: string | null;
  trans_details_interest_rate: number | null;
  rate_lock_loan_program: string | null;
  rate_lock_investor_commitment: string | null;
  investor_name: string | null;
  milestone: string | null;
  milestone_history: MilestoneHistoryItem[] | null;
  // Latest Encompass conversation-log timestamp; rescue signal for the
  // Activity tab's as-of-today "stalled" lens (~90% populated).
  last_conversation_log_at?: string | null;
  // Loan Center "stalled" calc inputs (shared loanStaleness service): current
  // stage-entry date + investor delivery date (for lock-met). Optional so the
  // other report fixtures/factories that build PipelineLoan literals still compile.
  milestone_date?: string | null;
  shipping_investor_delivery_date?: string | null;
  loan_folder: string;
  channel: string | null;
  creation_date?: string | null;
  application_date: string | null;
  closing_date: string | null;
  // Funded date from milestone completion (more accurate than closing_date)
  funded_date: string | null;
  // Milestone date fields for line report
  cx_ls_date_processing: string | null;
  cx_ls_date_uw: string | null;
  cd_sent_date: string | null;
  // Purchased date from milestone completion (banked loans)
  purchased_date: string | null;
  // Brokered loan fund date from payouts table
  broker_fund_date: string | null;
  // Team member fields for role inference
  loan_processor_first_name: string | null;
  loan_processor_last_name: string | null;
  underwriter_first_name: string | null;
  underwriter_last_name: string | null;
  closer_first_name: string | null;
  closer_last_name: string | null;
  loan_officer_assistant_first_name: string | null;
  loan_officer_assistant_last_name: string | null;
  loan_officer_2_first_name: string | null;
  loan_officer_2_last_name: string | null;
  // LTV fields (Encompass Field 353 and 976)
  freddie_mac_ltv_ratio_pct: number | null;
  trans_details_cltv_ratio_pct: number | null;
  // Lien position (Encompass Field 420) — "First Lien" or "Second Lien"
  lien_position: string | null;
  // Loan classification fields (used by Funded Summary Report)
  trans_details_loan_purpose?: string | null;
  trans_details_loan_type?: string | null;
  amortization_type?: string | null;
  // Non-QM doc-type signals (used by the Funded report's Non-QM subtype breakdown)
  cx_doc_type?: string | null;
  rate_lock_documentation_type?: string | null;
  dscr_ratio?: number | null;
}

// Helper to get full borrower name
export function getBorrowerName(loan: PipelineLoan): string {
  const parts = [loan.borrower_first_name, loan.borrower_last_name].filter(Boolean);
  return parts.join(' ') || '-';
}

// Helper to get full loan officer name
export function getLoanOfficerName(loan: PipelineLoan): string {
  const parts = [loan.loan_officer_first_name, loan.loan_officer_last_name].filter(Boolean);
  return parts.join(' ') || '-';
}

export type PipelineStatus =
  | 'started'
  | 'loan_setup'
  | 'processing'
  | 'submitted'
  | 'cond_approval'
  | 'resubmitted'
  | 'final_approval'
  | 'docs_out'
  | 'funded'
  | 'purchased';

export interface StatusSummary {
  status: PipelineStatus;
  label: string;
  count: number;
  totalAmount: number;
  color: string;
}

export type DatePreset = 'mtd' | 'ytd' | 'custom';

export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

export interface PipelineFilters {
  status: PipelineStatus | 'all';
  loanOfficer: string | 'all';
  investor: string | 'all';
  channel: string | 'all';
  lockedOnly: boolean;
  dateRange: DateRange;
}

export const STATUS_CONFIG: Record<PipelineStatus, { label: string; color: string }> = {
  started: { label: 'Started', color: 'bg-slate-100 text-slate-800' },
  loan_setup: { label: 'Loan Setup', color: 'bg-zinc-100 text-zinc-800' },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-800' },
  submitted: { label: 'Submitted', color: 'bg-indigo-100 text-indigo-800' },
  cond_approval: { label: 'Cond. Approval', color: 'bg-purple-100 text-purple-800' },
  resubmitted: { label: 'Resubmitted', color: 'bg-violet-100 text-violet-800' },
  final_approval: { label: 'Final Approval', color: 'bg-emerald-100 text-emerald-800' },
  docs_out: { label: 'Docs Out', color: 'bg-orange-100 text-orange-800' },
  funded: { label: 'Funded', color: 'bg-teal-100 text-teal-800' },
  purchased: { label: 'Purchased', color: 'bg-green-100 text-green-800' }
};

// Helper to get effective funded date based on channel
export function getEffectiveFundedDate(loan: PipelineLoan): string | null {
  // Brokered loans use fund_date from payouts table
  if (loan.channel === 'Brokered' && loan.broker_fund_date) {
    return loan.broker_fund_date;
  }
  // Banked/Retail loans use funded_date from milestone (more accurate)
  // Falls back to closing_date for backwards compatibility
  return loan.funded_date || loan.closing_date;
}

// Explicit mapping from Encompass milestone names to pipeline stages.
// Banked - Retail milestones are the source of truth; Brokered milestones
// map to their closest Banked equivalent.
export const MILESTONE_TO_STATUS: Record<string, PipelineStatus> = {
  // Started
  started: 'started',
  'file started': 'started',

  // Loan Setup
  'loan setup': 'loan_setup',

  // Processing
  processing: 'processing',
  'sent to processing': 'processing',

  // Submitted
  submitted: 'submitted',
  submittal: 'submitted',

  // Conditionally Approved
  'cond. approval': 'cond_approval',
  'approved w/ cond.': 'cond_approval',

  // Resubmitted
  resubmitted: 'resubmitted',

  // Final Approval
  'final approval': 'final_approval',
  'clear to close': 'final_approval',

  // Docs Out
  'docs out': 'docs_out',
  'docs sent': 'docs_out',

  // Funded
  funded: 'funded',
  funding: 'funded',

  // Purchased (banked loans only)
  purchased: 'purchased'
};

// Get the doneDate from milestone_history for a specific pipeline status.
// Used by the Line Report to chart when loans reached each milestone.
export function getMilestoneDoneDate(loan: PipelineLoan, targetStatus: PipelineStatus): string | null {
  if (!loan.milestone_history) {
return null;
}
  for (const m of loan.milestone_history) {
    if (m.done && m.doneDate) {
      const status = MILESTONE_TO_STATUS[m.name.toLowerCase().trim()];
      if (status === targetStatus) {
return m.doneDate;
}
    }
  }
  return null;
}

// Get the doneBy name from milestone_history for a specific pipeline status.
export function getMilestoneDoneBy(loan: PipelineLoan, targetStatus: PipelineStatus): string | null {
  if (!loan.milestone_history) {
return null;
}
  for (const m of loan.milestone_history) {
    if (m.done && m.doneDate) {
      const status = MILESTONE_TO_STATUS[m.name.toLowerCase().trim()];
      if (status === targetStatus) {
return m.doneBy || null;
}
    }
  }
  return null;
}

// Infer the role of a user who completed a milestone.
// First tries matching doneBy name against team member columns on the loan,
// then falls back to inferring from milestone name.
export function inferRole(doneBy: string, loan: PipelineLoan, milestoneName: string): string {
  const name = doneBy.toLowerCase().trim();
  // Match if doneBy contains both first and last name (handles middle names)
  const match = (first: string | null, last: string | null) => {
    if (!first || !last) {
return false;
}
    const f = first.toLowerCase().trim();
    const l = last.toLowerCase().trim();
    return name.includes(f) && name.includes(l);
  };

  if (match(loan.loan_officer_first_name, loan.loan_officer_last_name)) {
return 'Loan Officer';
}
  if (match(loan.loan_officer_2_first_name, loan.loan_officer_2_last_name)) {
return 'Loan Officer 2';
}
  if (match(loan.loan_officer_assistant_first_name, loan.loan_officer_assistant_last_name)) {
return 'LO Assistant';
}
  if (match(loan.loan_processor_first_name, loan.loan_processor_last_name)) {
return 'Processor';
}
  if (match(loan.underwriter_first_name, loan.underwriter_last_name)) {
return 'Underwriter';
}
  if (match(loan.closer_first_name, loan.closer_last_name)) {
return 'Closer';
}

  // Fallback: infer from milestone name
  const ms = milestoneName.toLowerCase().trim();
  if (['started', 'file started', 'loan setup'].includes(ms)) {
return 'Loan Officer';
}
  if (ms.includes('processing') || ms === 'sent to processing') {
return 'Processor';
}
  if (['submitted', 'submittal', 'resubmitted'].includes(ms)) {
return 'Processor';
}
  if (ms.includes('approval') || ms === 'clear to close' || ms.includes('approved')) {
return 'Underwriter';
}
  if (ms.includes('docs')) {
return 'Closer';
}
  if (ms.includes('fund') || ms === 'purchased') {
return 'Closer';
}

  return 'Operations';
}

// ─── Pick Up Report Types ──────────────────────────────────────────────────

export interface WaterfallAdjustment {
  reason: string;
  adjustor: number;
  adjustmentType: string;
}

export interface WaterfallMarkup {
  name: string;
  netMarkupPercent: number;
  netMarkupDollars?: number;
  totalMarkupDollars?: number;
}

export interface WaterfallCompensation {
  compensationPercent: number;
  loCompensationDollarAmount: number;
  loCompensationNetDollarAmount?: number;
  loCompensationMinCap?: number;
  loCompensationMaxCap?: number;
}

export interface WaterfallPricingDetail {
  rate: number;
  price: number;
  parRate: number;
  parPrice: number;
  lockPeriod: number;
  loCompensationPercent: number;
  loCompensationDollars: number;
  discountDollars?: number;
  discountPercent?: number;
  originationDollars?: number;
  originationPercent?: number;
  margin?: number;
  lenderFeesDollars?: number;
  netLenderFeesDollars?: number;
  thirdPartyFeesDollars?: number;
  netThirdPartyFeesDollars?: number;
  totalFeesDollars?: number;
  netTotalFeesDollars?: number;
  totalClosingCostDollars?: number;
  borrowerPaidClosingCostDollars?: number;
  pricingLastUpdated?: string;
}

export interface PricingWaterfall {
  adjustments: WaterfallAdjustment[];
  investorAdjustments: WaterfallAdjustment[];
  markups: WaterfallMarkup[];
  compensation: WaterfallCompensation | null;
  pricingDetail: WaterfallPricingDetail | null;
  investorPrice: number;
  investorRate: number;
}

export type PickupAccessStage = 'loan_detail' | 'product';

export interface PickupEligibleProduct {
  investor?: string | null;
  productName?: string | null;
  productId?: number | null;
  investorId?: number | null;
  rate?: number | null;
  price?: number | null;
  apr?: number | null;
  principalAndInterest?: number | null;
  lockPeriod?: number | null;
  loCompensationDollars?: number | null;
  loCompensationPercent?: number | null;
  closingCost?: number | null;
  role?: 'currentInvestor' | 'bestAlt' | null;
}

export interface ObLockCacheRow {
  id: string;
  ob_loan_id: number;
  los_loan_number: string | null;
  ob_originator_id: number;
  ob_entity_id: number;
  ob_business_channel_id?: number;
  borrower_name: string | null;
  ob_status: string | null;
  product_name: string | null;
  investor_name: string | null;
  locked_rate: number | null;
  locked_price: number | null;
  locked_investor_price: number | null;
  lock_period: number | null;
  lock_request_date: string | null;
  lock_expiration_date: string | null;
  base_loan_amount: number | null;
  last_refreshed_at: string | null;
  loan_detail_refreshed_at?: string | null;
  product_refreshed_at?: string | null;
  last_refresh_status?: string | null;
  last_refresh_error?: string | null;
  loan_detail_consecutive_failures?: number;
  loan_detail_last_attempt_at?: string | null;
  loan_detail_last_failure_at?: string | null;
  loan_detail_last_error?: string | null;
  product_consecutive_failures?: number;
  product_last_attempt_at?: string | null;
  product_last_failure_at?: string | null;
  product_last_error?: string | null;
  ob_access_parked_at?: string | null;
  ob_access_parked_stage?: PickupAccessStage | null;
  cache_frozen_at?: string | null;
  pickup_last_priced_at?: string | null;
  pickup_last_priced_lock_identity_hash?: string | null;
  pickup_next_pricing_eligible_at?: string | null;
  pickup_force_reprice_next_allowed_at?: string | null;
  pickup_verified_lock_hash?: string | null;
  pickup_verified_lock_hash_version?: number | null;
  pickup_verified_pricing_hash?: string | null;
  pickup_verified_pricing_hash_version?: number | null;
  pickup_verified_active_selection_id?: string | null;
  pickup_verified_pipeline_payload_hash?: string | null;
  pickup_verified_lock_duration_days?: number | null;
  pickup_verified_ob_lock_period_bucket?: number | null;
  pickup_verification_status?: PickupVerificationStatus | null;
  pickup_verification_episode_key?: string | null;
  pickup_verification_attempt_count?: number;
  pickup_verification_last_attempt_at?: string | null;
  pickup_verification_retry_after?: string | null;
  pickup_last_verified_at?: string | null;
  pickup_verification_status_updated_at?: string | null;
  pickup_verification_last_error?: string | null;
  pickup_verification_mismatch_fields?: PickupVerificationMismatch[];
}

export interface ObCacheRefreshRun {
  id: string;
  status: 'running' | 'completed' | 'completed_with_errors' | 'failed';
  pipeline_calls: number;
  loan_detail_calls: number;
  product_calls: number;
  rows_seen: number;
  rows_upserted: number;
  rows_changed: number;
  rows_skipped: number;
  frozen_recon_skips?: number;
  frozen_ob_rows?: number;
  frozen_unique_loans?: number;
  loan_detail_skipped_unchanged?: number;
  product_skipped_unchanged?: number;
  ob_access_parked_skips?: number;
  rows_error: number;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
  created_at: string;
  scope?: {
    mode?: string | null;
  } | null;
}

export interface PickupResultRow {
  id: string;
  analysis_id: string;
  ob_loan_id: number;
  los_loan_number: string | null;
  borrower_name: string | null;
  loan_amount: number | null;
  locked_investor: string;
  locked_product: string;
  locked_rate: number;
  locked_price: number;
  locked_investor_price: number | null;
  lock_date: string | null;
  lock_expiration: string | null;
  lock_period: number | null;
  basis_lock_period: number;
  best_investor: string | null;
  best_product: string | null;
  best_rate: number | null;
  best_price: number | null;
  pickup_points: number | null;
  pickup_dollars: number | null;
  has_pickup: boolean;
  lock_expired: boolean;
  eligible_products: PickupEligibleProduct[] | null;
  eligible_count: number;
  ob_search_id: string | null;
  search_ltv: number | null;
  search_cltv: number | null;
  current_investor_price: number | null;
  result_status: 'success' | 'no_match_at_rate' | 'no_alt_investor' | 'best_available' | 'error';
  result_message: string | null;
  encompass_loan_id: string | null;
  pricing_waterfall: PricingWaterfall | null;
  created_at: string;
}

export interface PickupAnalysisRow {
  id: string;
  created_by: string;
  status: string;
  total_loans: number;
  loans_with_pickup: number;
  total_pickup_dollars: number | null;
  avg_pickup_points: number | null;
  completed_at: string | null;
  error_message: string | null;
  created_at: string;
  requested_loans?: number;
  cooldown_rejected_loans?: number;
}

export interface PickupRunningAnalysisProgress {
  analysisId: string;
  completedCount: number;
  totalCount: number;
  startedAt: string;
}

export interface PickupAnalysisOpportunityAttempt {
  id: string;
  opportunityId: string;
  obLoanId: number;
  investorName: string;
  status: PickupAttemptStatus;
}

/** Merged row: encompass_loans base + OB lock cache + pickup results */
export interface PickupLoan {
  // From encompass_loans
  encompass_loan_id: string;
  trans_details_loan_number: string | null;
  borrower_first_name: string | null;
  borrower_last_name: string | null;
  loan_officer_first_name: string | null;
  loan_officer_last_name: string | null;
  trans_details_loan_amt: number | null;
  trans_details_interest_rate: number | null;
  rate_lock_status: string | null;
  rate_lock_buy_side_lock_date: string | null;
  rate_lock_buy_side_lock_expires: string | null;
  investor_name: string | null;
  rate_lock_investor_commitment: string | null;
  milestone: string | null;
  channel: string | null;
  funded_date: string | null;
  purchased_date: string | null;

  // From OB lock cache (enriched via Refresh)
  ob_status: string | null;
  ob_loan_id: number | null;
  locked_price: number | null;
  locked_investor_price: number | null;
  locked_rate: number | null;
  lock_period: number | null;
  ob_investor_name: string | null;
  ob_product_name: string | null;
  lock_expiration_date: string | null;
  base_loan_amount: number | null;
  ob_cached: boolean;
  ob_access_parked_at: string | null;
  ob_access_parked_stage: PickupAccessStage | null;
  ob_access_error: string | null;
  pickup_last_priced_at: string | null;
  pickup_last_priced_lock_identity_hash: string | null;
  pickup_next_pricing_eligible_at: string | null;
  pickup_force_reprice_next_allowed_at: string | null;
  pickup_verification_status: PickupVerificationStatus | null;
  pickup_verification_mismatch_fields: PickupVerificationMismatch[];
  pickup_verification_last_error: string | null;
  pickup_verification_retry_after: string | null;
  pickup_last_verified_at: string | null;
  pickup_verified_lock_duration_days: number | null;
  pickup_verified_ob_lock_period_bucket: number | null;
  pickup_canonical_state_changed: boolean;
  open_pickup_opportunity: PickupOpenOpportunityBadge | null;

  // From pickup results (enriched via Analyze)
  best_investor: string | null;
  best_product: string | null;
  best_price: number | null;
  best_rate: number | null;
  pickup_points: number | null;
  pickup_dollars: number | null;
  has_pickup: boolean;
  lock_expired: boolean;
  eligible_products: PickupEligibleProduct[] | null;
  eligible_count: number;
  analyzed: boolean;
  analyzed_at: string | null;
  current_investor_price: number | null;
  result_status: string | null;
  result_message: string | null;
  needs_review_reason: string | null;
  basis_lock_period: number | null;
  pricing_waterfall: PricingWaterfall | null;
}

export type PickupEncompassLoan = Pick<
  PickupLoan,
  | 'encompass_loan_id'
  | 'trans_details_loan_number'
  | 'borrower_first_name'
  | 'borrower_last_name'
  | 'loan_officer_first_name'
  | 'loan_officer_last_name'
  | 'trans_details_loan_amt'
  | 'trans_details_interest_rate'
  | 'rate_lock_status'
  | 'rate_lock_buy_side_lock_date'
  | 'rate_lock_buy_side_lock_expires'
  | 'investor_name'
  | 'rate_lock_investor_commitment'
  | 'milestone'
  | 'channel'
  | 'funded_date'
  | 'purchased_date'
> & PickupCanonicalLoanInput & {
  pickup_current_lock_hash?: string | null;
  pickup_current_pricing_hash?: string | null;
  pickup_current_active_selection_id?: string | null;
};

export interface PickupRunAnalysisResponse {
  analysisId: string;
  alreadyRunning: boolean;
  message?: string;
  requestedCount: number;
  uniqueRequestedCount: number;
  duplicateCount?: number;
  invalidCount?: number;
  cacheRejectedCount?: number;
  validationRejectedCount?: number;
  freshnessRejectedCount?: number;
  cooldownRejectedCount?: number;
  eligibleCount?: number;
  analyzedCount?: number;
  rejectedCount?: number;
  committedFundedRejectedCount?: number;
  opportunityOpenedCount?: number;
  opportunityStillOpenCount?: number;
  manualRepriceAudit?: Record<string, unknown>;
  results: unknown[];
}

export function isPickupLoanParked(loan: Pick<PickupLoan, 'ob_access_parked_at'>): boolean {
  return !!loan.ob_access_parked_at;
}

export function isPickupLoanCommittedAndFunded(
  loan: Partial<Pick<PickupLoan, 'rate_lock_investor_commitment' | 'funded_date'>>
): boolean {
  return String(loan.rate_lock_investor_commitment ?? '').trim().length > 0
    && String(loan.funded_date ?? '').trim().length > 0;
}

export function pickupLoanCooldownUntil(
  loan: Pick<PickupLoan, 'pickup_next_pricing_eligible_at'>,
  nowMs = Date.now()
): string | null {
  if (!loan.pickup_next_pricing_eligible_at) {
    return null;
  }
  const nextEligibleMs = Date.parse(loan.pickup_next_pricing_eligible_at);
  return Number.isFinite(nextEligibleMs) && nextEligibleMs > nowMs
    ? new Date(nextEligibleMs).toISOString()
    : null;
}

export function isPickupLoanSelectable(
  loan: Pick<
    PickupLoan,
    | 'ob_cached'
    | 'ob_access_parked_at'
    | 'needs_review_reason'
  > & Partial<Pick<
    PickupLoan,
    | 'pickup_verification_status'
    | 'pickup_canonical_state_changed'
    | 'rate_lock_investor_commitment'
    | 'funded_date'
  >>
): boolean {
  const verificationSelectable = loan.pickup_canonical_state_changed
    || !loan.pickup_verification_status
    || [
      'verified',
      'verified_with_drift',
      'pending_verification',
      'ob_access_error'
    ].includes(loan.pickup_verification_status);
  return loan.ob_cached
    && !isPickupLoanParked(loan)
    && !isPickupLoanCommittedAndFunded(loan)
    && verificationSelectable
    && loan.needs_review_reason === null;
}

export function getSelectablePickupLoanIds<T extends Pick<
  PickupLoan,
  | 'encompass_loan_id'
  | 'ob_cached'
  | 'ob_access_parked_at'
  | 'needs_review_reason'
> & Partial<Pick<
  PickupLoan,
  | 'pickup_verification_status'
  | 'pickup_canonical_state_changed'
  | 'rate_lock_investor_commitment'
  | 'funded_date'
>>>(loans: T[]): string[] {
  return loans.filter(isPickupLoanSelectable).map((loan) => loan.encompass_loan_id);
}

export function pickupBasisDiffers(
  loan: Pick<PickupLoan, 'analyzed' | 'basis_lock_period' | 'lock_period'>
): boolean {
  return loan.analyzed
    && loan.basis_lock_period !== null
    && loan.basis_lock_period !== loan.lock_period;
}

// Derive pipeline status from milestone_history (last completed milestone by position).
// milestone_history is always populated by the webhook and is the source of truth.
export function derivePipelineStatus(loan: PipelineLoan): PipelineStatus {
  // Walk backwards — array is ordered by workflow position (Started first, Completion last)
  for (let i = (loan.milestone_history?.length ?? 0) - 1; i >= 0; i--) {
    const m = loan.milestone_history![i];
    if (m.done) {
      const status = MILESTONE_TO_STATUS[m.name.toLowerCase().trim()];
      if (status) {
return status;
}
      // "Completion" not in lookup table — keep walking to find Purchased/Funded
    }
  }
  return 'started';
}
